import { useContext, useEffect, useState } from "react";
import colors from "./colors";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import GridViewIcon from "@mui/icons-material/GridView";
import { deleteFavorites, getFavorites, updateFavorites } from "./homeService";
import { AuthContext } from "core/contexts/auth/auth";
import { useNavigate } from "react-router";
import { setActiveModel } from "modules/chat/store/conversationList";
import { setBreadCrumbs, setHeader } from "core/store/shared/header";
import { useAppDispatch, useAppSelector } from "core/store/reduxStore";
import { Toast as showToast } from "core/components/global/toast/toast";
import { SidebarContext } from "core/contexts/sidebar/sidebar";
import { useAppTheme } from "core/contexts/theme/theme";
import {
  TagContainerStyle,
  TagTitleP,
  TagAvailableNowCheckbox,
  TagButton,
  TagButtonsContainer,
  TagAvailableNowLayoutButtons,
} from "./home.styled";
import { Paper } from "@mui/material";
import { Tag } from "./tag";
import { t } from "i18next";
import { setFavoritesCount } from "core/store/shared/sideBar";
import { FilteredItemList } from "./filteredItem";
import { navigateToPath } from "core/utils/common";

const FilteredItems = ({ tagsData, isLandingPage = false }) => {
  const { checkAMSToken } = useContext(AuthContext);
  const { modelData } = useContext(SidebarContext);
  const navigate = useNavigate();
  const [tagsList, setTags] = useState(tagsData);
  const [sidebarObjs, setSidebarObjs] = useState(tagsData);
  const [favorites, setfavorites] = useState<any>([]);
  const [viewMode, setViewMode] = useState("grid");
  const [showAvailableOnly, setShowAvailableOnly] = useState(false);
  const [filterTags, setFilterTags] = useState<any>([]);
  const [filteredResults, setFilteredResults] = useState<any>([]);
  const { apptheme } = useAppTheme();

  const dispatch = useAppDispatch();

  useEffect(() => {
    setFilterTags([]);
    setTags(tagsData);
    setSidebarObjs(tagsData);

    async function fetchFavorites() {
      const token = await checkAMSToken();
      const data = await getFavorites(token);
      setfavorites(data);
    }

    fetchFavorites();

    return () => {
      setTags([]);
    };
  }, [tagsData]);

  // Extract unique tags from all models
  function extractUniqueTags(data) {
    const allTags = new Set();

    function traverseTree(items) {
      for (let item of items) {
        // Look for tags in sidebarProps
        if (item.sidebarProps && item.sidebarProps.tags) {
          item.sidebarProps.tags.forEach((tag) => allTags.add(tag));
        }

        // Recursively check children
        if (item.children) {
          traverseTree(item.children);
        }
      }
    }

    traverseTree(data);
    console.log("allTags", allTags);
    return Array.from(allTags);
  }

  // Find leaf nodes (models without children)
  function findLeafNodes(data) {
    let models: any = [];

    function searchTree(items) {
      for (let item of items) {
        if (!item.children || item.children.length === 0) {
          // This is a leaf node
          models.push(item);
        } else {
          // Continue searching
          searchTree(item.children);
        }
      }
    }

    searchTree(data);
    return models;
  }

  function removeDuplicates(arr) {
    const seen = new Set();
    return (
      arr &&
      arr.filter((item) => {
        const stringified = JSON.stringify(item);
        if (!seen.has(stringified)) {
          seen.add(stringified);
          return true;
        }
        return false;
      })
    );
  }

  const getFilteredModel = () => {
    let models: any = findLeafNodes(sidebarObjs);

    // Filter by availability
    if (showAvailableOnly) {
      models = models.filter((model) => !model.path.includes("/soon"));
    }

    // Filter by selected tags
    if (filterTags.length > 0) {
      models = models.filter((model) => {
        if (!model.sidebarProps.tags) return false;

        // Check if model has at least one of the selected tags
        return filterTags.some((tag) => model.sidebarProps.tags.includes(tag));
      });
    }

    models = models.filter((model) => !model.path.includes("/capabilities/"));

    return removeDuplicates(models);
  };

  useEffect(() => {
    const response = getFilteredModel();
    setFilteredResults(response);
  }, [showAvailableOnly, filterTags]);

  useEffect(() => {
    const response = getFilteredModel();
    setFilteredResults(response);
  }, []);

  const updateFavoritesState = async (state) => {
    const token = await checkAMSToken();
    
    // Guard against null or undefined favorites
    if (!favorites) {
      console.error("Favorites is null or undefined in updateFavoritesState");
      showToast("Unable to update favorites at this time. Please try again later.", "error");
      return false;
    }
    
    const existFavorite: any = favorites.find(
      (provider) => provider.targetName === state
    );

    // If the favorite can be found locally proceed
    if (existFavorite) {
      const response = await deleteFavorites(token, existFavorite.targetName);

      // check for an error, if so do not update the local
      if (response === true) {
        const newList = favorites.filter((t) => t._id !== existFavorite._id);
        setfavorites(newList);
        dispatch(setFavoritesCount(newList.length));
        return true;
      } else {
        return false;
      }
    } else {
      const response = await updateFavorites(token, state);

      // A failed response returns null, success returns the new object
      if (response !== null && Object.keys(response).length > 0) {
        let newList = favorites ? [...favorites] : []; // Create a new array, ensuring favorites is not null
        newList.push(response);
        setfavorites(newList);
        dispatch(setFavoritesCount(newList.length));
        return true;
      } else {
        return false;
      }
    }
  };

  const toggleTagFilter = (tag) => {
    if (filterTags.includes(tag)) {
      setFilterTags(filterTags.filter((t) => t !== tag));
    } else {
      setFilterTags([...filterTags, tag]);
    }
  };

  function getBreadcrumbAndPath(targetDisplayText) {
    const breadcrumb: any = [];
    let targetPath: any = null;
    let targetParameter: any = null;

    function findItem(items) {
      for (const item of items) {
        if (item.sidebarProps.displayText === targetDisplayText) {
          breadcrumb.push({displayText:item.sidebarProps.displayText,path:item.path});
          if ("children" in item) {
            // Its a directory not a root, it should not even be here
            targetPath = "dir";
          } else {
            targetPath = item.path;
            if ("parameter" in item) {
              targetParameter = item.parameter;
            }
          }
          return true;
        }
        if (item.children) {
          breadcrumb.push({displayText:item.sidebarProps.displayText,path:item.path});
          if (findItem(item.children)) {
            return true;
          } else {
            breadcrumb.pop();
          }
        }
      }
      return false;
    }

    findItem(modelData);
    return { breadcrumb, path: targetPath, parameter: targetParameter };
  }

  const handleChatNavigation = (displayText) => {
    const crumb: any = getBreadcrumbAndPath(displayText);
    console.log("1 breadcrumb", crumb);
    console.log("displayText", displayText);

    if (crumb.path !== "" && crumb.path !== "dir") {
      dispatch(setBreadCrumbs(crumb.breadcrumb));
    }

    navigateToPath(navigate, crumb, handleSetActiveModel);
  };

  const handleSetActiveModel = (model) => {
    dispatch(setActiveModel(model));
  }

  // Get unique tags for filtering
  const uniqueTags: any = extractUniqueTags(filteredResults);

  return (
    <TagContainerStyle className="flex-1 overflow-y-auto p-6 bg-gray-200/10 border-slate-700 shadow-lg transition-all duration-300 ease-in-out block">
      {isLandingPage && (
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold mb-2" style={{ color: apptheme.colors.textColor }}>
            mySecurity AI Engine
          </h1>
          <p className="text-md mb-4" style={{ color: apptheme.colors.textColor }}>
            Select a model or capability to get started
          </p>
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <div className="item-options flex items-center space-x-3">
          <TagAvailableNowLayoutButtons
            viewmode={viewMode}
            className="rounded-md shadow-sm p-1 flex"
          >
            <button
              className="p-1.5 rounded"
              data-active={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
            >
              <GridViewIcon sx={{ fontSize: 18, color: `${viewMode === "grid" ? apptheme.colors.textColor : apptheme.colors.textColor} !important` }} />
            </button>
            <button
              className="p-1.5 rounded"
              data-active={viewMode === "list"}
              onClick={() => setViewMode("list")}
            >
              <FormatListNumberedIcon sx={{ fontSize: 18, color: `${viewMode === "list" ? apptheme.colors.textColor : apptheme.colors.textColor} !important` }} />
            </button>
          </TagAvailableNowLayoutButtons>
          <TagAvailableNowCheckbox className="flex items-center px-3 py-1 rounded-md shadow-sm text-sm form-check-label">
            <div className="form-check">
              <input
                type="checkbox"
                className="mr-2 form-check-input"
                id="radio1"
                name="optradio"
                value="option1"
                checked={showAvailableOnly}
                style={{
                  position: "static",
                  top: "6.5rem",
                  left: "14rem",
                  translate: "0 25%",
                }}
                onChange={() => setShowAvailableOnly(!showAvailableOnly)}
              />
              <label htmlFor="radio1">
                <p style={{ color: apptheme.colors.textColor }}>
                  Available now
                </p>
              </label>
            </div>
          </TagAvailableNowCheckbox>
        </div>
      </div>
      <div className="text-sm font-medium mb-2">
        <p
          className="mb-3"
          style={{
            color: apptheme.colors.textColor,
            fontWeight: "bold",
            marginLeft: "0.25rem"
          }}
        >
          Filter by tags:
        </p>
      </div>
      <div className="mb-6">
        <TagButtonsContainer className="flex flex-wrap gap-2">
          {uniqueTags.map((tag, tagIdx) => (
            <Tag
              key={`tag-filter-${tagIdx}`}
              tag={tag}
              index={tagIdx}
              toggleTagFilter={toggleTagFilter}
              filterTags={filterTags}
              colors={colors}
            />
          ))}
        </TagButtonsContainer>
      </div>

      

      {viewMode === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredResults.map((filteredItem, index) => (
            <FilteredItemList
              key={`grid-item-${index}`}
              index={index}
              filteredItem={filteredItem}
              favorites={favorites}
              handleChatNavigation={handleChatNavigation}
              updateFavoritesState={updateFavoritesState}
              handleSetActiveModel={handleSetActiveModel}
              type={"grid"}
            />
          ))}
        </div>
      )}

      {viewMode === "list" && (
        <div className="space-y-3">
          {filteredResults.map((filteredItem, index) => (
            <FilteredItemList
              key={`list-item-${index}`}
              index={index}
              filteredItem={filteredItem}
              favorites={favorites}
              handleChatNavigation={handleChatNavigation}
              updateFavoritesState={updateFavoritesState}
              handleSetActiveModel={handleSetActiveModel}
              type={"list"}
            />
          ))}
        </div>
      )}
    </TagContainerStyle>
  );
};

export default FilteredItems;
